import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const GET_SEMESTERS_URL = `${API_URL}/Custom/GetSemesters`;
const GET_SEMESTER_COURSES_URL = `${API_URL}/Custom/GetSemesterCourses`;
const GET_TIMESLOTS_URL = `${API_URL}/Custom/GetCourseTimeSlots`;
const GET_CLASSROOMS_URL = `${API_URL}/Custom/GetClassrooms`;
const GET_ORGANIZATIONS_URL = `${API_URL}/Custom/GetOrganizations`;
const POST_SCHEDULE_URL = `${API_URL}/Custom/ScheduleCourses`;
const GET_SCHEDULE_BY_CLASSROOM_URL = `${API_URL}/Custom/GetUniversityWeeklyScheduleByClassroom`;
const GET_EVENTS_URL = `${API_URL}/Custom/GetCourseScheduleEvents`;
const POST_EVENT_URL = `${API_URL}/Custom/CreateCourseScheduleEvent`;
const DELETE_EVENT_URL = `${API_URL}/Custom/DeleteCourseScheduleEvent`;

export default function CourseSchedulerComponent() {
  const [step, setStep] = useState(1);

  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const [allCourses, setAllCourses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);

  const [courseGroups, setCourseGroups] = useState([]);
  const [selectedForGroup, setSelectedForGroup] = useState([]);
  const [disabledAutoGroupNames, setDisabledAutoGroupNames] = useState([]);

  const [classrooms, setClassrooms] = useState([]);
  const [selectedClassrooms, setSelectedClassrooms] = useState([]);

  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);

  const [status, setStatus] = useState('');

  const todayIso = () => new Date().toISOString().slice(0, 10);
  const nextMonthIso = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().slice(0, 10);
  };

  const [eventTitle, setEventTitle] = useState('');
  const [eventDetail, setEventDetail] = useState('');
  const [eventDate, setEventDate] = useState(todayIso());
  const [eventTimeSlot, setEventTimeSlot] = useState(null);
  const [eventClassroom, setEventClassroom] = useState(null);
  const [eventListClassroom, setEventListClassroom] = useState(null);
  const [eventStartDate, setEventStartDate] = useState(todayIso());
  const [eventEndDate, setEventEndDate] = useState(nextMonthIso());
  const [events, setEvents] = useState([]);
  const [eventSchedule, setEventSchedule] = useState([]);
  const [eventLoading, setEventLoading] = useState(false);
  const [eventSaving, setEventSaving] = useState(false);
  const [eventError, setEventError] = useState('');

  useEffect(() => {
    axios.get(GET_SEMESTERS_URL)
      .then(res => setSemesters(res.data || []))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedSemester) return;

    axios.get(`${GET_SEMESTER_COURSES_URL}?semesterId=${selectedSemester}`)
      .then(res => {
        setAllCourses(res.data || []);
        setCourses([]);
      })
      .catch(console.error);

    axios.get(`${GET_TIMESLOTS_URL}?semesterId=${selectedSemester}`)
      .then(res => setTimeSlots(res.data || []))
      .catch(console.error);

    axios.get(GET_CLASSROOMS_URL)
      .then(res => setClassrooms(res.data || []))
      .catch(console.error);

    axios.get(GET_ORGANIZATIONS_URL)
      .then(res => setOrganizations((res.data || []).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(console.error);

    // reset downstream
    setSelectedOrganization(null);
    setSelectedCourses([]);
    setCourseGroups([]);
    setSelectedForGroup([]);
    setDisabledAutoGroupNames([]);
    setSelectedClassrooms([]);
    setSelectedTimeSlots([]);
    setEventTitle('');
    setEventDetail('');
    setEventDate(todayIso());
    setEventTimeSlot(null);
    setEventClassroom(null);
    setEventListClassroom(null);
    setEventStartDate(todayIso());
    setEventEndDate(nextMonthIso());
    setEvents([]);
    setEventSchedule([]);
    setEventError('');
  }, [selectedSemester]);

  useEffect(() => {
    if (!selectedOrganization) {
      setCourses([]);
      return;
    }
    setCourses(allCourses.filter(c => c.rOrganizationId === selectedOrganization));
  }, [selectedOrganization, allCourses]);

  const autoCourseGroups = useMemo(() => {
    const byName = selectedCourses.reduce((acc, id) => {
      const c = allCourses.find(x => x.id === id);
      if (!c) return acc;
      acc[c.name] = acc[c.name] || [];
      acc[c.name].push(id);
      return acc;
    }, {});
    return Object.entries(byName)
      .filter(([name, ids]) => ids.length > 1 && !disabledAutoGroupNames.includes(name))
      .map(([, ids]) => ids);
  }, [selectedCourses, allCourses, disabledAutoGroupNames]);

  const allGroups = useMemo(() => [
    ...autoCourseGroups,
    ...courseGroups
  ], [autoCourseGroups, courseGroups]);

  const ungroupedCourses = useMemo(() => {
    const grouped = new Set(allGroups.flat());
    return selectedCourses.filter(id => !grouped.has(id));
  }, [selectedCourses, allGroups]);

  const selectedCourseObjs = allCourses.filter(c => selectedCourses.includes(c.id));
  const coursesByOrg = selectedCourseObjs.reduce((acc, c) => {
    const name = organizations.find(o => o.id === c.rOrganizationId)?.name || 'Diğer';
    acc[name] = acc[name] || [];
    acc[name].push(c);
    return acc;
  }, {});

  const loadEvents = useCallback(() => {
    if (!selectedSemester) {
      setEvents([]);
      setEventSchedule([]);
      setEventLoading(false);
      return;
    }
    setEventLoading(true);
    setEventError('');
    const params = new URLSearchParams();
    params.append('semesterId', selectedSemester);
    if (eventStartDate) params.append('startDate', eventStartDate);
    if (eventEndDate) params.append('endDate', eventEndDate);
    if (eventListClassroom) params.append('classroomId', eventListClassroom);

    axios
      .get(`${GET_EVENTS_URL}?${params.toString()}`)
      .then(res => setEvents(res.data || []))
      .catch(err => setEventError(err.response?.data || err.message))
      .finally(() => setEventLoading(false));

    if (eventListClassroom) {
      axios
        .get(`${GET_SCHEDULE_BY_CLASSROOM_URL}?semesterId=${selectedSemester}&classroomId=${eventListClassroom}`)
        .then(res => setEventSchedule(res.data || []))
        .catch(console.error);
    } else {
      setEventSchedule([]);
    }
  }, [selectedSemester, eventStartDate, eventEndDate, eventListClassroom]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleCreateEvent = async () => {
    if (!selectedSemester || !eventTimeSlot || !eventClassroom || !eventDate) {
      setEventError('Lütfen tüm alanları doldurun.');
      return;
    }
    if (!eventTitle.trim()) {
      setEventError('Etkinlik adı zorunludur.');
      return;
    }

    setEventError('');
    setEventSaving(true);
    try {
      await axios.post(POST_EVENT_URL, {
        SemesterId: selectedSemester,
        TimeSlotId: eventTimeSlot,
        ClassroomId: eventClassroom,
        Date: eventDate,
        Title: eventTitle,
        Detail: eventDetail
      });
      setEventTitle('');
      setEventDetail('');
      setEventTimeSlot(null);
      if (!eventListClassroom && eventClassroom) {
        setEventListClassroom(eventClassroom);
      }
      loadEvents();
    } catch (err) {
      setEventError(err.response?.data || err.message);
    } finally {
      setEventSaving(false);
    }
  };

  const handleDeleteEvent = (id) => {
    if (!window.confirm('Etkinliği silmek istediğinize emin misiniz?')) return;
    axios
      .delete(`${DELETE_EVENT_URL}?id=${id}`)
      .then(() => loadEvents())
      .catch(err => setEventError(err.response?.data || err.message));
  };

  const renderEventSchedule = () => {
    if (!eventListClassroom || !eventSchedule.length) return null;
    const grouped = eventSchedule.reduce((acc, item) => {
      const day = item.day || 0;
      if (!acc[day]) acc[day] = [];
      acc[day].push(item);
      return acc;
    }, {});
    const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
    return (
      <div className='mt-4'>
        <h5>Haftalık Ders Programı</h5>
        <div className='row'>
          {Object.entries(grouped).map(([day, items]) => {
            const d = Number(day);
            const label = dayNames[d - 1] || `Gün ${day}`;
            return (
              <div key={day} className='col-md-4 mb-3'>
                <div className='border rounded h-100 p-3'>
                  <h6 className='fw-semibold'>{label}</h6>
                  <table className='table table-sm table-bordered mb-0'>
                    <thead>
                      <tr>
                        <th>Saat</th>
                        <th>Başlık</th>
                        <th>Tür</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.id}>
                          <td>{item.startTime} - {item.endTime}</td>
                          <td>
                            {item.courseName}
                            {item.isEvent && item.eventDate ? (
                              <div className='text-muted small'>{item.eventDate}</div>
                            ) : null}
                          </td>
                          <td>{item.isEvent ? 'Etkinlik' : (item.name ? item.name.split(' - ').pop() : '')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const createGroup = () => {
    if (selectedForGroup.length < 2) return;
    setCourseGroups(prev => [...prev, selectedForGroup]);
    setSelectedForGroup([]);
  };

  const removeGroup = idx => setCourseGroups(prev => prev.filter((_, i) => i !== idx));
  const cancelAutoGroup = name => setDisabledAutoGroupNames(prev => [...prev, name]);

  const formatSlots = () => {
    const byDay = {};
    selectedTimeSlots.forEach(id => {
      const ts = timeSlots.find(x => x.id === id);
      if (!ts) return;
      const day = `Gün ${ts.day}`;
      const time = `${ts.startTime.substring(0,5)}-${ts.endTime.substring(0,5)}`;
      byDay[day] = byDay[day] || [];
      byDay[day].push(time);
    });
    return byDay;
  };

  const handleSubmit = async () => {
    setStatus('Planlanıyor...');
    try {
      const payloadGroups = [
        ...autoCourseGroups,
        ...courseGroups,
        ...ungroupedCourses.map(id => [id])
      ];
      await axios.post(POST_SCHEDULE_URL, {
        SemesterId: selectedSemester,
        SemesterCourseIds: selectedCourses,
        ClassroomIds: selectedClassrooms,
        TimeSlotIds: selectedTimeSlots,
        CourseGroups: payloadGroups
      });
      setStatus('Dersler başarıyla planlandı!');
    } catch (err) {
      setStatus('Hata: ' + (err.response?.data || err.message));
    }
  };

  return (
    <div className='container mt-4'>
      <h2 className='mb-4'>Ders Planlayıcı</h2>
      <ul className='nav nav-pills mb-4'>
        {['Dönem', 'Organizasyon & Ders', 'Sınıf & Slot', 'Onay'].map((lbl, i) => (
          <li className='nav-item' key={i}>
            <span className={`nav-link ${step === i + 1 ? 'active' : ''}`}>
              {i + 1}. {lbl}
            </span>
          </li>
        ))}
      </ul>

      <div className='card p-4'>
        <div className='mb-4 d-flex justify-content-between'>
          <button className='btn btn-secondary' onClick={() => setStep(s => Math.max(s - 1, 1))} disabled={step === 1}>Geri</button>
          <button className='btn btn-secondary' onClick={() => setStep(s => Math.min(s + 1, 4))} disabled={step === 4}>İleri</button>
        </div>

        {step === 1 && (
          <>
            <h5>1. Dönem Seç</h5>
            <select className='form-control' value={selectedSemester || ''} onChange={e => setSelectedSemester(Number(e.target.value))}>
              <option value='' disabled>Seçiniz</option>
              {semesters.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </>
        )}

        {step === 2 && (
          <>
            <h5>2. Organizasyon & Ders Seç</h5>
            <select className='form-control mb-3' value={selectedOrganization || ''} onChange={e => setSelectedOrganization(Number(e.target.value))}>
              <option value='' disabled>Organizasyon Seçiniz</option>
              {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>

            <div className='row'>
              <div className='col-md-6'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h6 className='mb-0'>Tüm Dersler</h6>
                  <button className='btn btn-sm btn-outline-primary' onClick={() => {
                    const ids = courses.map(c => c.id);
                    setSelectedCourses(prev => Array.from(new Set([...prev, ...ids])));
                  }}>Tümünü Seç</button>
                </div>
                {courses.map(c => {
                  const checked = selectedCourses.includes(c.id);
                  return (
                    <div key={c.id} className='form-check'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={checked}
                        onChange={() => setSelectedCourses(prev => checked ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                      />
                      <label className='form-check-label ml-2'>{c.name}</label>
                    </div>
                  );
                })}
              </div>

              <div className='col-md-6'>
                <h6>Seçilen Dersler</h6>
                {Object.entries(coursesByOrg).map(([org, list]) => (
                  <div key={org} className='mb-3'>
                    <strong>{org}</strong>
                    <ul className='list-group list-group-flush'>
                      {list.map(c => <li className='list-group-item' key={c.id}>{c.name}</li>)}
                    </ul>
                  </div>
                ))}

                {autoCourseGroups.length > 0 && (
                  <>
                    <h6 className='mt-4'>Otomatik Gruplar</h6>
                    {autoCourseGroups.map((group, idx) => {
                      const name = allCourses.find(c => c.id === group[0])?.name;
                      return (
                        <div key={idx} className='border p-2 mb-2 d-flex justify-content-between align-items-center'>
                          <div>
                            <strong>“{name}” Grubu:</strong> {group.length} ders
                          </div>
                          <button className='btn btn-sm btn-outline-warning ml-2' onClick={() => cancelAutoGroup(name)}>İptal Et</button>
                        </div>
                      );
                    })}
                  </>
                )}

                <h6 className='mt-4'>Manuel Ders Gruplama</h6>
                {ungroupedCourses.length === 0 ? (
                  <p className='text-muted'>Gruplandırılacak ders yok.</p>
                ) : (
                  ungroupedCourses.map(id => {
                    const course = allCourses.find(c => c.id === id);
                    return (
                      <div className='form-check' key={id}>
                        <input
                          type='checkbox'
                          className='form-check-input'
                          checked={selectedForGroup.includes(id)}
                          onChange={() => setSelectedForGroup(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                        />
                        <label className='form-check-label ml-2'>{course?.name}</label>
                      </div>
                    );
                  })
                )}
                <button className='btn btn-sm btn-outline-success mt-2' disabled={selectedForGroup.length < 2} onClick={createGroup}>
                  Grupları Oluştur
                </button>

                {courseGroups.map((group, idx) => (
                  <div key={idx} className='border p-2 my-2'>
                    <strong>Grup {idx + 1}:</strong>{' '}
                    {group.map(id => allCourses.find(c => c.id === id)?.name).join(', ')}
                    <button className='btn btn-sm btn-outline-danger ml-2' onClick={() => removeGroup(idx)}>Sil</button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h5>3. Sınıf ve Zaman Seç</h5>
            <div className='mb-3'>
              <label>Sınıflar</label>
              {classrooms.map(r => {
                const checked = selectedClassrooms.includes(r.id);
                return (
                  <div key={r.id} className='form-check'>
                    <input className='form-check-input' type='checkbox' checked={checked} onChange={() => setSelectedClassrooms(prev => checked ? prev.filter(x => x !== r.id) : [...prev, r.id])} />
                    <label className='form-check-label ml-2'>{r.code || r.name}</label>
                  </div>
                );
              })}
            </div>
            <div>
              <label>Slotlar</label>
              {timeSlots.map(ts => {
                const checked = selectedTimeSlots.includes(ts.id);
                const display = ts.day ? `Gün ${ts.day} ${ts.startTime?.substring(0,5)}-${ts.endTime?.substring(0,5)}` : `${ts.startTime?.substring(0,5)}-${ts.endTime?.substring(0,5)}`;
                return (
                  <div key={ts.id} className='form-check'>
                    <input className='form-check-input' type='checkbox' checked={checked} onChange={() => setSelectedTimeSlots(prev => checked ? prev.filter(x => x !== ts.id) : [...prev, ts.id])} />
                    <label className='form-check-label ml-2'>{display}</label>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <h5>4. Onay</h5>
            <div>
              <h6>Slotlar</h6>
              {Object.entries(formatSlots()).map(([day, times]) => (
                <div key={day} className='mb-2'><strong>{day}:</strong> {times.join(', ')}</div>
              ))}
            </div>

            {autoCourseGroups.length > 0 && (
              <div className='mt-4'>
                <h6>Otomatik Gruplar</h6>
                {autoCourseGroups.map((group, idx) => {
                  const name = allCourses.find(c => c.id === group[0])?.name;
                  return <div key={idx}><strong>“{name}” Grubu:</strong> {group.length} ders</div>;
                })}
              </div>
            )}

            {courseGroups.length > 0 && (
              <div className='mt-4'>
                <h6>Manuel Gruplar</h6>
                {courseGroups.map((group, idx) => (
                  <div key={idx} className='mb-2'>
                    <strong>Grup {idx + 1}:</strong>
                    <ul className='mb-0'>
                      {group.map(id => {
                        const c = allCourses.find(x => x.id === id);
                        return <li key={id}>{c?.name}</li>;
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            <h6 className='mt-4'>Tekli Dersler</h6>
            {ungroupedCourses.map(id => {
              const c = allCourses.find(x => x.id === id);
              return <div key={id}>{c?.name}</div>;
            })}

            <h6 className='mt-4'>Sınıflar</h6>
            {selectedClassrooms.map(id => {
              const r = classrooms.find(x => x.id === id);
              return <div key={id}>{r?.code || r?.name}</div>;
            })}

            <button className='btn btn-primary mt-4' onClick={handleSubmit}>Planla</button>
            {status && <div className='alert alert-info mt-3'>{status}</div>}
          </>
        )}
      </div>
      {selectedSemester && (
        <div className='card mt-5 p-4'>
          <h4 className='mb-4'>Etkinlik Planlama</h4>
          {eventError && <div className='alert alert-danger'>{eventError}</div>}
          <div className='row'>
            <div className='col-md-3 mb-3'>
              <label className='form-label'>Tarih</label>
              <input
                type='date'
                className='form-control'
                value={eventDate}
                onChange={e => setEventDate(e.target.value)}
              />
            </div>
            <div className='col-md-3 mb-3'>
              <label className='form-label'>Saat</label>
              <select
                className='form-control'
                value={eventTimeSlot || ''}
                onChange={e => setEventTimeSlot(e.target.value ? Number(e.target.value) : null)}
              >
                <option value=''>Seçiniz</option>
                {timeSlots.map(ts => {
                  const display = ts.day
                    ? `Gün ${ts.day} ${ts.startTime?.substring(0, 5)}-${ts.endTime?.substring(0, 5)}`
                    : `${ts.startTime?.substring(0, 5)}-${ts.endTime?.substring(0, 5)}`;
                  return (
                    <option key={ts.id} value={ts.id}>
                      {display}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className='col-md-3 mb-3'>
              <label className='form-label'>Sınıf</label>
              <select
                className='form-control'
                value={eventClassroom || ''}
                onChange={e => setEventClassroom(e.target.value ? Number(e.target.value) : null)}
              >
                <option value=''>Seçiniz</option>
                {classrooms.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.code || r.name}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-3 mb-3'>
              <label className='form-label'>Etkinlik Adı</label>
              <input
                className='form-control'
                value={eventTitle}
                onChange={e => setEventTitle(e.target.value)}
              />
            </div>
            <div className='col-12 mb-3'>
              <label className='form-label'>Açıklama</label>
              <textarea
                className='form-control'
                rows={2}
                value={eventDetail}
                onChange={e => setEventDetail(e.target.value)}
              />
            </div>
            <div className='col-12'>
              <button className='btn btn-primary' onClick={handleCreateEvent} disabled={eventSaving}>
                {eventSaving ? 'Kaydediliyor...' : 'Etkinlik Oluştur'}
              </button>
            </div>
          </div>

          <hr className='my-4' />

          <h5 className='mb-3'>Etkinlik Listesi</h5>
          <div className='row'>
            <div className='col-md-3 mb-3'>
              <label className='form-label'>Başlangıç Tarihi</label>
              <input
                type='date'
                className='form-control'
                value={eventStartDate}
                onChange={e => setEventStartDate(e.target.value)}
              />
            </div>
            <div className='col-md-3 mb-3'>
              <label className='form-label'>Bitiş Tarihi</label>
              <input
                type='date'
                className='form-control'
                value={eventEndDate}
                onChange={e => setEventEndDate(e.target.value)}
              />
            </div>
            <div className='col-md-3 mb-3'>
              <label className='form-label'>Sınıf</label>
              <select
                className='form-control'
                value={eventListClassroom || ''}
                onChange={e => setEventListClassroom(e.target.value ? Number(e.target.value) : null)}
              >
                <option value=''>Tümü</option>
                {classrooms.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.code || r.name}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-3 mb-3 d-flex align-items-end'>
              <button className='btn btn-outline-secondary w-100' onClick={loadEvents} disabled={eventLoading}>
                Yenile
              </button>
            </div>
          </div>

          <div className='table-responsive'>
            <table className='table table-bordered'>
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Saat</th>
                  <th>Sınıf</th>
                  <th>Etkinlik</th>
                  <th>Açıklama</th>
                  <th className='text-end'>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {eventLoading ? (
                  <tr>
                    <td colSpan={6}>Yükleniyor...</td>
                  </tr>
                ) : events.length === 0 ? (
                  <tr>
                    <td colSpan={6}>Kayıt bulunamadı.</td>
                  </tr>
                ) : (
                  events.map(evt => {
                    const rawDate = evt.date ?? evt.Date;
                    const dateText = typeof rawDate === 'string' ? rawDate.substring(0, 10) : '';
                    const start = evt.startTime ?? evt.StartTime;
                    const end = evt.endTime ?? evt.EndTime;
                    const classroomName = evt.classroomName ?? evt.ClassroomName;
                    const title = evt.title ?? evt.Title;
                    const detail = evt.detail ?? evt.Detail;
                    return (
                      <tr key={evt.id}>
                        <td>{dateText}</td>
                        <td>{start} - {end}</td>
                        <td>{classroomName}</td>
                        <td>{title}</td>
                        <td>{detail || ''}</td>
                        <td className='text-end'>
                          <button className='btn btn-sm btn-outline-danger' onClick={() => handleDeleteEvent(evt.id)}>
                            Sil
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {renderEventSchedule()}
        </div>
      )}
    </div>
  );
}

